import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@ah/design';
import { Linkedin, AlertCircle } from 'lucide-react';
import { gql } from '../api';
import type { Me } from '../useAuth';

const SUBMIT = /* GraphQL */ `
  mutation($url: String!) {
    submitLinkedInUrl(url: $url) {
      id
      linkedinUrl
      linkedinUrlVerified
    }
  }
`;

const TRIGGER_IMPORT = /* GraphQL */ `
  mutation { triggerLinkedInImport }
`;

export function LinkedInUrlGate({ me }: { me: Me }) {
  const [url, setUrl] = useState(me.linkedinUrl ?? '');
  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: (url: string) => gql.request<unknown>(SUBMIT, { url }),
    onSuccess: () => {
      // Trigger background LinkedIn profile import (creates alumni circles).
      gql.request(TRIGGER_IMPORT).catch(() => {});
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  return (
    <div className="min-h-dvh grid place-items-center px-6 bg-gradient-to-b from-[hsl(34,45%,96%)] to-background">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <CardTitle className="text-2xl font-bold flex items-center gap-2">
            <Linkedin size={20} /> One more step
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Underlings requires a verifiable LinkedIn profile that matches the name on
            your account ({me.name}). Paste your public LinkedIn URL below.
          </p>
          <div className="space-y-2">
            <Label htmlFor="li-url">LinkedIn profile URL</Label>
            <Input
              id="li-url"
              placeholder="https://www.linkedin.com/in/your-handle/"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoFocus
            />
          </div>
          {submit.error ? (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>
                {(submit.error as Error).message ||
                  'We could not verify that URL. Make sure the profile name matches your LinkedIn account name exactly.'}
              </span>
            </div>
          ) : null}
          <Button
            className="w-full"
            disabled={!url.trim() || submit.isPending}
            onClick={() => submit.mutate(url.trim())}
          >
            {submit.isPending ? 'Verifying…' : 'Verify & continue'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
